/* ============================================================
   Shareable result links.

   An operator's finding is worth more when it can reach the person who
   can act on it — a colleague, a lender, a board. Until now every
   result died on the device that computed it.

   The design borrows its discipline from the sister JK site, which
   encodes scorecard answers into a query parameter and validates them
   strictly on the way back in. What could not be borrowed is the
   encoding itself: JK's works because its scorecard is a fixed grid of
   forty single-digit answers, so a fixed-length string of one character
   each is enough and its own checksum. A corridor run is a handful of
   numbers of wildly different magnitude — 485 km beside 1,120 dollars
   beside 2 days — so it needs a real field list.

   Four rules, each of which exists because the obvious alternative is
   wrong:

   1. INPUTS ONLY, never results. A link carries what was typed and the
      receiving device recomputes. Encoding the computed figures would
      let a link display numbers this engine would no longer produce —
      a stale duty rate, a superseded band — under the authority of
      whatever the reader currently sees. The recompute is the point.

   2. VERSIONED, and a version it does not know is refused outright.
      Fields will be added. A v1 link silently read as v2 would land
      values in the wrong slots and compute a confident wrong answer,
      which is worse than a link that says it cannot be read.

   3. VALIDATED WHOLE, applied whole. A link with one unparseable field
      is rejected entirely rather than partly applied. Half a run
      prefilled is worse than none, because it looks deliberate.

   4. NO PRECISION INVENTED. Values round-trip as typed. If someone
      entered 26, the link says 26, not 26.0000001 via a float.

   On privacy: the link contains the operator's own commercial figures —
   what a lane costs them, what they charge for it. Nothing is
   transmitted to build one, and the site still talks to no server. But
   pasting that link into a group chat does put those numbers somewhere,
   and the person doing it deserves to be told at the moment they copy,
   not in a notice they read once. That warning belongs in the UI beside
   the button; this module only makes the string.
   ============================================================ */

/** Bumped when FIELDS changes in any way that moves a value's position. */
export const SHARE_VERSION = 1;

export const SHARE_PARAM = 's';

/* Order is the format. Appending is safe; inserting, removing or
   reordering is not, and requires SHARE_VERSION to move. */
const FIELDS = [
  ['corridorKey', 'string'],
  ['mode', 'string'],
  ['distance', 'number'],
  ['payload', 'number'],
  ['containers', 'number'],
  ['tripCost', 'number'],
  ['tripRevenue', 'number'],
  ['cif', 'number'],
  ['dutyBandId', 'string'],
  ['freeDays', 'number'],
  ['daysHeld', 'number'],
  ['dso', 'number'],
  ['fuelLitres', 'number'],
  ['operatorType', 'string']
];

const SEP = '~';

/* Absent and zero are different answers. A run with no CIF declared is
   not a run with a CIF of nothing, and collapsing the two would put a
   duty line on a shipment that never had one. */
const ABSENT = '';

function encodeValue(value, type) {
  if (value === undefined || value === null || value === '') return ABSENT;
  if (type === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : ABSENT;
  }
  // Separator and URL-significant characters must not survive into a field.
  return String(value).replace(/[~&#?=%]/g, '');
}

/**
 * Build the query-string value for a set of run inputs.
 * @returns {string} safe for a URL without further escaping
 */
export function encodeRun(input) {
  if (!input || typeof input !== 'object') return '';
  const parts = FIELDS.map(([key, type]) => encodeValue(input[key], type));
  return `${SHARE_VERSION}${SEP}${parts.join(SEP)}`;
}

/**
 * Read a link back. Returns null for anything it cannot read completely
 * — a partially applied form is worse than an empty one.
 * @returns {{input: object, fields: number}|null}
 */
export function decodeRun(encoded) {
  if (typeof encoded !== 'string' || !encoded) return null;

  const parts = encoded.split(SEP);
  const version = Number(parts[0]);

  // A version this build does not know cannot be read safely, and
  // guessing would land values in the wrong fields.
  if (!Number.isInteger(version) || version !== SHARE_VERSION) return null;

  const values = parts.slice(1);
  // Trailing fields may be absent if a future version appends, but a
  // link that is SHORT of this version's field list is malformed.
  if (values.length !== FIELDS.length) return null;

  const input = {};
  let fields = 0;

  for (let i = 0; i < FIELDS.length; i += 1) {
    const [key, type] = FIELDS[i];
    const raw = values[i];
    if (raw === ABSENT) continue;

    if (type === 'number') {
      const n = Number(raw);
      // Reject rather than skip: a number that will not parse means the
      // link is damaged, and the rest of it cannot be trusted either.
      if (!Number.isFinite(n)) return null;
      input[key] = n;
    } else {
      input[key] = raw;
    }
    fields += 1;
  }

  // An empty link is not a link. Something must have been carried.
  if (fields === 0) return null;

  return { input, fields };
}

/** Full URL for a run, from the current origin. */
export function shareUrl(path, input, origin = window.location.origin) {
  const encoded = encodeRun(input);
  return encoded ? `${origin}${path}?${SHARE_PARAM}=${encoded}` : `${origin}${path}`;
}

/** Read a shared run out of a location search string, if present. */
export function readShared(search = window.location.search) {
  const value = new URLSearchParams(search).get(SHARE_PARAM);
  return value ? decodeRun(value) : null;
}
